'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import AdSense from '@/components/adsense/AdSense';

interface TrendingItem {
  id: string;
  title: string;
  image: string;
  imageAlt: string;
  views: number;
  category: string;
  timestamp: string;
}

interface RightSidebarProps {
  trendingItems?: TrendingItem[];
}

const defaultTrendingItems: TrendingItem[] = [
{
  id: 'trend_1',
  title: 'Breaking: Global Climate Summit Reaches Historic Agreement',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1781b7ace-1767117542231.png",
  imageAlt: 'World leaders at climate summit',
  views: 2450000,
  category: 'Politics',
  timestamp: '2 hours ago'
},
{
  id: 'trend_2',
  title: 'Tech Giant Unveils Revolutionary AI Assistant',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdbd0cf6-1768685982828.png",
  imageAlt: 'AI technology concept',
  views: 1890000,
  category: 'Technology',
  timestamp: '3 hours ago'
},
{
  id: 'trend_3',
  title: 'Olympic Champion Announces Surprise Retirement',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1701d70b1-1770655946218.png",
  imageAlt: 'Olympic athlete celebrating',
  views: 1560000,
  category: 'Sports',
  timestamp: '5 hours ago'
},
{
  id: 'trend_4',
  title: 'Stock Market Hits All-Time High Amid Economic Recovery',
  image: "https://images.unsplash.com/photo-1691643158804-d3f02eb456a3",
  imageAlt: 'Stock market charts',
  views: 1340000,
  category: 'Business',
  timestamp: '6 hours ago'
},
{
  id: 'trend_5',
  title: 'Hollywood Blockbuster Breaks Box Office Records',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10d9c9496-1770655943961.png",
  imageAlt: 'Movie theater premiere',
  views: 1120000,
  category: 'Entertainment',
  timestamp: '8 hours ago'
},
{
  id: 'trend_6',
  title: 'Medical Breakthrough: New Cancer Treatment Shows Promise',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_176a8bbb8-1767962095181.png",
  imageAlt: 'Medical research laboratory',
  views: 980000,
  category: 'Health',
  timestamp: '10 hours ago'
},
{
  id: 'trend_7',
  title: 'Space Agency Discovers Earth-Like Planet',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1658a7039-1770655945102.png",
  imageAlt: 'Space telescope view of planet',
  views: 875000,
  category: 'Science',
  timestamp: '12 hours ago'
},
{
  id: 'trend_8',
  title: 'Fashion Week: Sustainable Designs Take Center Stage',
  image: "https://images.unsplash.com/photo-1583460338311-5fbd9acbb0d1",
  imageAlt: 'Fashion runway show',
  views: 720000,
  category: 'Fashion',
  timestamp: '14 hours ago'
}];


export default function RightSidebar({ trendingItems = defaultTrendingItems }: RightSidebarProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const formatViews = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      Politics: 'from-blue-500 to-blue-600',
      Technology: 'from-cyan-500 to-cyan-600',
      Sports: 'from-green-500 to-green-600',
      Business: 'from-orange-500 to-orange-600',
      Entertainment: 'from-pink-500 to-pink-600',
      Health: 'from-teal-500 to-teal-600',
      Science: 'from-purple-500 to-purple-600',
      Fashion: 'from-fuchsia-500 to-fuchsia-600'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };

  const getRankGradient = (index: number): string => {
    if (index === 0) return 'from-yellow-400 via-yellow-500 to-orange-500';
    if (index === 1) return 'from-gray-300 via-gray-400 to-gray-500';
    if (index === 2) return 'from-orange-300 via-orange-400 to-orange-500';
    return 'from-gray-100 to-gray-200';
  };

  return (
    <aside className="space-y-6">
      {/* Trending Section with Modern Design */}
      <div className="bg-gradient-to-br from-white via-red-50/30 to-orange-50/30 rounded-2xl shadow-xl border border-red-100 overflow-hidden sticky top-24 backdrop-blur-sm">
        {/* Header with Animated Gradient */}
        <div className="relative bg-gradient-to-r from-red-500 via-orange-500 to-red-600 px-5 py-4 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-yellow-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-white blur-md opacity-50 rounded-full"></div>
                <div className="relative bg-white/20 backdrop-blur-sm p-2 rounded-full">
                  <Icon name="FireIcon" size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-white tracking-tight">
                  Trending Now
                </h2>
                <p className="text-xs text-white/90 font-medium">Most viewed stories</p>
              </div>
            </div>
            
            {/* Live Indicator */}
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-xs font-bold text-white">LIVE</span>
            </div>
          </div>
        </div>
        
        {/* Trending Items with Enhanced Design */}
        <div className="divide-y divide-red-100/50">
          {trendingItems.slice(0, 8).map((item, index) =>
          <a
            key={item.id}
            href={`/articles/${item.id}`}
            className="block p-4 hover:bg-gradient-to-r hover:from-red-50/50 hover:to-orange-50/50 transition-all duration-300 group relative"
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}>
            
              {/* Hover Glow Effect */}
              {activeIndex === index &&
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-orange-500/5 pointer-events-none"></div>
            }
              
              <div className="relative flex gap-3">
                {/* Rank Badge with Gradient */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    {index < 3 &&
                  <div className={`absolute inset-0 bg-gradient-to-r ${getRankGradient(index)} blur-md opacity-50 rounded-full`}></div>
                  }
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-lg ${
                  index === 0 ? 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 text-white' :
                  index === 1 ? 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 text-white' :
                  index === 2 ? 'bg-gradient-to-br from-orange-300 via-orange-400 to-orange-500 text-white' : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600'} transform group-hover:scale-110 transition-transform duration-300`
                  }>
                      {index < 3 ?
                    <Icon name="FireIcon" size={18} /> :

                    <span>{index + 1}</span>
                    }
                    </div>
                  </div>
                </div>
                
                {/* Thumbnail Image */}
                <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden shadow-md group-hover:shadow-xl transition-shadow duration-300">
                  <AppImage
                  src={item.image}
                  alt={item.imageAlt}
                  width={64}
                  height={64}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 group-hover:text-red-600 transition-colors mb-2 leading-tight">
                    {item.title}
                  </h3>
                  
                  {/* Meta Information */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Category Badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${getCategoryColor(item.category)} shadow-sm`}>
                      {item.category}
                    </span>
                    
                    {/* Views */}
                    <span className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                      <Icon name="EyeIcon" size={14} className="text-red-500" />
                      <span className="font-bold text-red-600">{formatViews(item.views)}</span>
                    </span>
                    
                    {/* Timestamp */}
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Icon name="ClockIcon" size={12} />
                      {item.timestamp}
                    </span>
                  </div>
                </div>
                
                {/* Arrow Icon on Hover */}
                <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Icon name="ChevronRightIcon" size={20} className="text-red-500" />
                </div>
              </div>
            </a>
          )}
        </div>
        
        {/* Footer with Quick Stats */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 px-5 py-3 border-t border-red-100">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-gray-600">
              <Icon name="ChartBarIcon" size={14} className="text-red-500" />
              <span className="font-medium">Updated every minute</span>
            </div>
            <div className="flex items-center gap-1 text-red-600 font-bold">
              <Icon name="ArrowTrendingUpIcon" size={14} />
              <span>Live</span>
            </div>
          </div>
        </div>
        
        {/* AdSense Ad Space */}
        <div className="bg-white px-4 py-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center mb-2 font-medium">Advertisement</div>
          <AdSense
            adClient={process.env.NEXT_PUBLIC_ADSENSE_ID || ''}
            adSlot="1234567890"
            adFormat="auto"
            adStyle={{ display: 'block', minHeight: '250px' }} />
          
        </div>
      </div>
    </aside>);

}